 import { useState, useReducer, useRef, useEffect } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { getOuterHeight } from '@Components/Utilities/SandhillsUtility';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import usePassiveLayoutEffect from '@react-hook/passive-layout-effect';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import facetedSearchReducer from '@Components/Shared/FacetedSearch/FacetedSearchReducer';
import ready from 'document-ready';
import WebSavedSearchesButton from '@Components/WebSavedSearches/WebSavedSearchesButton';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';

const FacetedSearchSelectedFacet = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchSelectedFacet'));
const FacetedSearchModal = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchModal'), true);
const FacetedSearchField = loadable(() => import('@Components/Shared/FacetedSearch/FacetedSearchField'));

const FacetedSearch = (props) => {

    const windowSize = useWindowSize();
    const selectedFacetsRef = useRef(null);
    const [facetedSearchModal, dispatchFacetedSearchModal] = useReducer(facetedSearchReducer, { Options: [], ShowLoadingSpinner: true });
    const [showFacetedSearchModal, setShowFacetedSearchModal] = useState(props.openFacetedModalOnLoad);
    const [showAllSelectedOptions, setShowAllSelectedOptions] = useState(false);
    const currentUser = useRecoilValue(atoms.currentUser);

    useEffect(() => {
        if (props.openFacetedModalOnLoad) {
            FacetedSearchModal.preload();
        }
    }, []);

    useEffect(() => {
        if (props.highlightSelectedFacets) {
            const timer = setTimeout(() => {
                props.onHighlightEnd?.();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [props.highlightSelectedFacets]);

    usePassiveLayoutEffect(() => {
        ready(() => adjustCollapsedAppliedFacetHeight(!showAllSelectedOptions));
    }, [props.SearchFields, props.SelectedFacets]);

    const onFacetOpen = () => {
        FacetedSearchSelectedFacet.preload();
    };

    const toggleSelectedOptions = () => {
        adjustCollapsedAppliedFacetHeight(showAllSelectedOptions);
        setShowAllSelectedOptions(showAllSelectedOptions => !showAllSelectedOptions);
    };

    const getCollapsedAppliedFacetMaxHeight = () => {
        return FacetedSearchSelectedFacet.load().then((f) => {
            return (getOuterHeight(selectedFacetsRef.current.firstChild, true) * 2) + 5;
        });
    };

    const adjustCollapsedAppliedFacetHeight = (adjustMaxHeight) => {
        if (selectedFacetsRef.current && selectedFacetsRef.current.firstChild) {
            getCollapsedAppliedFacetMaxHeight().then(maxHeight => {
                if (adjustMaxHeight) {
                    selectedFacetsRef.current.style.whiteSpace = 'nowrap';
                    selectedFacetsRef.current.style.maxHeight = maxHeight + 'px';
                } else {
                    selectedFacetsRef.current.style.whiteSpace = 'normal';
                    selectedFacetsRef.current.style.maxHeight = '';
                }

                if (selectedFacetsRef.current.clientHeight < maxHeight) {
                    document.getElementsByClassName('facets-toggle')?.[0]?.setAttribute('style', 'display: none;');
                } else {
                    document.getElementsByClassName('facets-toggle')?.[0]?.setAttribute('style', '');
                }
            });
        }
    };

    const renderSelectedFacets = () => {
        let facets = props.SelectedFacets ? props.SelectedFacets : props.SearchFields;
        return (facets.map(field =>
            <FacetedSearchSelectedFacet
                {...field}
                key={'FacetedSearchSelectedFacet' + field.FieldName}
                ClearFacet={props.clearFacet}
                SearchURL={props.SearchBaseURL}
                disabled={props.disabled}
            />)
        );
    };

    return (
        <>
            <div id='filters' className={'faceted-search-content no-print' + (props.showMobileFilters ? ' mobile-visible' : '') + (props.disabled ? ' facets-disabled' : '')}>
                <h3 className='visually-hidden'>{translator.translate('sandhills.appliedfilters')}</h3>
                {windowSize.isMobile &&
                    <div className='mobile-done-button-container'>
                        <a className='mobile-done-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.toggleMobileFilter(); } }} onClick={props.toggleMobileFilter}>
                            <TranslationText tKey='SandhillsSystem.Done' />
                        </a>
                    </div>}
                {(props.ListingsCount <= 0 && (props.renderError && typeof props.renderError === 'function')) && props.renderError()}
                {(!props.IsDefaultSearch || (props.ListingsCount <= 0 && !props.IsWatchList && !props.IsEventsPage) || (props.EnableWebSavedSearches && currentUser.IsAuthenticated && !windowSize.isMobile)) &&
                    <div className='faceted-search__top-section'>
                        {windowSize.isMobile &&
                            <div className='mobile-filtering-by'>
                                <TranslationText tKey='sandhills.filteringby' />:
                            </div>}
                        <div
                            className={`selected-facets-container${props.highlightSelectedFacets ? ' filters-highlight' : ''}`}
                        >
                            {(!props.IsDefaultSearch || (props.ListingsCount <= 0 && !props.IsWatchList)) &&
                                <>
                                    <div className='selected-header cf'>
                                        <strong><TranslationText tKey='sandhills.appliedfilters' /></strong>
                                        <button className='ts-button filter-clear-all' onClick={props.clearAll}><TranslationText tKey='sandhills.clearall' /></button>
                                    </div>
                                    <div ref={selectedFacetsRef} className='selected-facets'>
                                        {renderSelectedFacets()}
                                    </div>
                                    <div className='facets-toggle' style={{ display: 'none' }}>
                                    <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleSelectedOptions(); } }} onClick={toggleSelectedOptions}>
                                            {showAllSelectedOptions ?
                                                <TranslationText key='sandhills.showfeweroptions' tKey='sandhills.showfeweroptions' /> :
                                                <TranslationText key='sandhills.viewallselectedoptions' tKey='sandhills.viewallselectedoptions' />}
                                        </a>
                                    </div>
                                </>
                            }
                            {((props.EnableWebSavedSearches && !props.TurnOffDefaultSaveThisSearch && props.savedSearchData && !windowSize.isMobile) || (!windowSize.isMobile && props.EnableWebSavedSearches && ((!props.IsDefaultSearch && !props.TurnOffDefaultSaveThisSearch) || currentUser.IsAuthenticated))) &&
                                <WebSavedSearchesButton {...props.savedSearchData} IsDefaultSearch={props.IsDefaultSearch} TurnOffDefaultSaveThisSearch={props.TurnOffDefaultSaveThisSearch}
                                    openModal={props.openSavedSearchModal} closeModal={props.closeSavedSearchModal}
                                    getWebSavedSearches={props.getWebSavedSearches} removeWebSavedSearch={props.removeWebSavedSearch}
                                saveCurrentSearch={props.saveCurrentSearch} updateWebSavedSearch={props.updateWebSavedSearch} setPendingSave={props.setPendingSave} />
                            }
                        </div>
                    </div>}
                <div className='faceted-search__main-section' id='faceted-search-main'>
                    {props.isAttachmentPage && props.SearchFields.filter(item => !item.IsFitsTo && !item.GroupName && item.Type !== 'Hidden' && (item.FieldName == 'keywords' || item.FieldName == 'LotNumber' || item.FieldName == 'InventoryType' || item.FieldName == 'WLID')).map(item =>
                        <FacetedSearchField
                            {...item}
                            key={item.FieldName + (item.SpecGroupName ? ('-' + item.SpecGroupName) : '')}
                            onFacetOpen={onFacetOpen}
                            RunSearch={props.runSearch}
                            MobileSearch={props.mobileSearch}
                            SearchURL={props.SearchBaseURL}
                            ShowMobileFilters={props.showMobileFilters}
                            ToggleMobileFilter={props.toggleMobileFilter}
                            HasOptionsSelected={(props.SelectedFacets && props.SelectedFacets.length > 0 && props.SelectedFacets.some(facet => facet.FieldName === item.FieldName || facet.SpecGroupName === item.FieldName))}
                            DispatchFacetedSearchModal={dispatchFacetedSearchModal}
                            SetShowModal={setShowFacetedSearchModal}
                            ShowModal={showFacetedSearchModal}
                            IsEmptySearch={props.ListingsCount <= 0 && !((props.isDealerInventoryLandingPage && props?.EmptyDealerLandingPageEnabledFacets?.includes(item.FieldName)) || item.EnableOnEmptySearch)}
                            ClearFacet={props.clearFacet}
                            UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                            ClassificationToForceScope={props.ClassificationToForceScope}
                            setShowLoadingRing={props.setShowLoadingRing}
                            listingType={props.listingType}
                        />)}
                    {props.SearchFields.some(item => item.IsPopularCategory) &&
                        <div className='grouped-faceted-header'>
                            <TranslationText tKey='sandhills.tradepubwebsite.mvc.popularcategories' />
                        </div>}
                    {props.SearchFields.filter(item => item.IsPopularCategory).map(item =>
                        <FacetedSearchField
                            {...item}
                            key={item.FieldName}
                            onFacetOpen={onFacetOpen}
                            RunSearch={props.runSearch}
                            SearchURL={props.SearchBaseURL}
                            ShowMobileFilters={props.showMobileFilters}
                            MobileSearch={props.mobileSearch}
                            DispatchFacetedSearchModal={dispatchFacetedSearchModal}
                            SetShowModal={setShowFacetedSearchModal}
                            ShowModal={showFacetedSearchModal}
                            IsEmptySearch={props.ListingsCount <= 0}
                            ClearFacet={props.clearFacet}
                            UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                            ClassificationToForceScope={props.ClassificationToForceScope}
                            setShowLoadingRing={props.setShowLoadingRing}
                            listingType={props.listingType}
                        />)}
                    {props.isAttachmentPage &&
                        <div className='grouped-faceted-header'>
                            <TranslationText tKey='sandhills.tradesites.mvc.attachmentcategory' />
                        </div>
                    }
                    {props.SearchFields.filter(item => !item.IsFitsTo && !item.IsPopularCategory && !item.GroupName && item.Type !== 'Hidden' && (!props.isAttachmentPage || (props.isAttachmentPage && item.FieldName !== 'keywords' && item.FieldName !== 'LotNumber' && item.FieldName !== 'InventoryType' && item.FieldName !== 'WLID'))).map(item =>
                        <FacetedSearchField
                            {...item}
                            key={item.FieldName + (item.SpecGroupName ? ('-' + item.SpecGroupName) : '')}
                            onFacetOpen={onFacetOpen}
                            RunSearch={props.runSearch}
                            MobileSearch={props.mobileSearch}
                            SearchURL={props.SearchBaseURL}
                            ShowMobileFilters={props.showMobileFilters}
                            ToggleMobileFilter={props.toggleMobileFilter}
                            HasOptionsSelected={(props.SelectedFacets && props.SelectedFacets.length > 0 && props.SelectedFacets.some(facet => facet.FieldName === item.FieldName || facet.SpecGroupName === item.FieldName))}
                            DispatchFacetedSearchModal={dispatchFacetedSearchModal}
                            SetShowModal={setShowFacetedSearchModal}
                            ShowModal={showFacetedSearchModal}
                            IsEmptySearch={props.ListingsCount <= 0 && !((props.isDealerInventoryLandingPage && props?.EmptyDealerLandingPageEnabledFacets?.includes(item.FieldName)) || item.EnableOnEmptySearch)}
                            ClearFacet={props.clearFacet}
                            UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                            ClassificationToForceScope={props.ClassificationToForceScope}
                            KeywordSearchParamsDetected={props.KeywordSearchParamsDetected}
                            ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                            setShowLoadingRing={props.setShowLoadingRing}
                            listingType={props.listingType}
                        />)}
                    {props.SearchFields.some(item => item.IsFitsTo) &&
                        <div className='grouped-faceted-header'>
                            <TranslationText tKey='sandhills.tradepubwebsite.mvc.fitstoinformation' />
                        </div>}
                    {props.SearchFields.filter(item => item.IsFitsTo).map(item =>
                        <FacetedSearchField
                            {...item}
                            key={item.FieldName}
                            onFacetOpen={onFacetOpen}
                            RunSearch={props.runSearch}
                            SearchURL={props.SearchBaseURL}
                            ShowMobileFilters={props.showMobileFilters}
                            MobileSearch={props.mobileSearch}
                            HasOptionsSelected={(props.SelectedFacets && props.SelectedFacets.length > 0 && props.SelectedFacets.some(facet => facet.FieldName === item.FieldName || facet.SpecGroupName === item.FieldName))}
                            DispatchFacetedSearchModal={dispatchFacetedSearchModal}
                            SetShowModal={setShowFacetedSearchModal}
                            ShowModal={showFacetedSearchModal}
                            IsEmptySearch={props.ListingsCount <= 0}
                            ClearFacet={props.clearFacet}
                            UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                            ClassificationToForceScope={props.ClassificationToForceScope}
                            setShowLoadingRing={props.setShowLoadingRing}
                            listingType={props.listingType}
                        />)}
                    {props.SearchFields.some(item => !item.IsFitsTo && !item.IsPopularCategory && item.GroupName) &&
                        <div className='grouped-faceted-header'>
                            {props.isAttachmentPage ? <TranslationText tKey='sandhills.tradesites.mvc.attachmentdetails' /> : <TranslationText tKey='sandhills.additionalfilters' />}
                        </div>}
                    {props.SearchFields.filter(item => !item.IsFitsTo && !item.IsPopularCategory && item.GroupName).map(item =>
                        <FacetedSearchField
                            {...item}
                            key={item.FieldName}
                            onFacetOpen={onFacetOpen}
                            RunSearch={props.runSearch}
                            SearchURL={props.SearchBaseURL}
                            ShowMobileFilters={props.showMobileFilters}
                            ToggleMobileFilter={props.toggleMobileFilter}
                            MobileSearch={props.mobileSearch}
                            DispatchFacetedSearchModal={dispatchFacetedSearchModal}
                            SetShowModal={setShowFacetedSearchModal}
                            ShowModal={showFacetedSearchModal}
                            IsEmptySearch={props.ListingsCount <= 0 && !((props.isDealerInventoryLandingPage && props?.EmptyDealerLandingPageEnabledFacets?.includes(item.FieldName)) || item.EnableOnEmptySearch)}
                            UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                            ClassificationToForceScope={props.ClassificationToForceScope}
                            setShowLoadingRing={props.setShowLoadingRing}
                            listingType={props.listingType}
                        />)}
                </div>
            </div>
            {showFacetedSearchModal &&
                <FacetedSearchModal
                    {...facetedSearchModal}
                    UseNextUpFacets={props.useNextUpFacets}
                />}
        </>
    );
};

export default FacetedSearch;